import { Button, Container, Heading, useBreakpointValue, VStack } from '@chakra-ui/react';
import * as React from 'react';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatPreComposer } from '../data/routes';
import { Authenticated } from './AuthGuards';
import { Backer } from './Backer';
import Emoji from './Emoji';
import { Fade } from './Fade';
import { Fetching } from './Fetching';
import { useLoadLetter } from './hooks/useLoadLetter';
import LetterView, { SideCarLetterView } from './LetterView';

type WriteBackButtonProps = { senderId: string };

const WriteBackButton = ({ senderId }: WriteBackButtonProps) => {
  const navigate = useNavigate();

  const onClick = useCallback(() => {
    navigate(formatPreComposer({ friendId: senderId }));
  }, [senderId, navigate]);

  return (
    <Button variant="solid" onClick={onClick}>
      <Emoji symbol="✍️" label="Hand holding a pen" spaceRight /> Compose reply
    </Button>
  );
};

type LetterViewerProps = { letterId: string };

export const LetterViewer = ({ letterId }: LetterViewerProps) => {
  const responsivePadding = useBreakpointValue({ base: 4, xs: 8, lg: 16 });
  const { data: letter, isLoading } = useLoadLetter(letterId);

  return (
    <Fade>
      <Container maxW="container.lg" p={responsivePadding} pt={0}>
        <Fetching isFetching={isLoading}>
          {letter && (
            <VStack w="100%" alignItems="flex-start" spacing={8}>
              <Backer />
              <VStack w="100%" alignItems="flex-start" spacing={[2, 2, 4]}>
                <Heading size="md">
                  {letter.directionality === 'sent' && <>To: </>}
                  {letter.directionality === 'received' && <>From:</>} {letter.correspondent_name}
                </Heading>
                <LetterView letter={letter} />
                {letter.directionality === 'received' && (
                  <WriteBackButton senderId={letter.sender} />
                )}
              </VStack>
            </VStack>
          )}
        </Fetching>
      </Container>
    </Fade>
  );
};

export const MiniLetterViewer = ({ letterId }: { letterId: string }) => {
  const { data: letter, isLoading } = useLoadLetter(letterId);

  return (
    <Fade>
      <Fetching isFetching={isLoading}>
        <div style={{ width: '100%' }}>{letter && <SideCarLetterView letter={letter} />}</div>
      </Fetching>
    </Fade>
  );
};

type Params = { letterId: string };

export const AuthenticatedLetterViewer = () => {
  const { letterId } = useParams<Params>() as Params;

  return (
    <Authenticated>
      <LetterViewer letterId={letterId} />
    </Authenticated>
  );
};
